import React from "react";
import { Server, ShieldOff, ShieldCheck } from "lucide-react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RedisHealthCard — Sub-sprint D-α.
 *
 * Read-only health snapshot of the rate-limit backend. Surfaces ONLY
 * non-sensitive fields per the backend contract (no URL, no creds, no
 * provider name). `customer_facing_allowed` is rendered prominently
 * and MUST be `false` per contract; if `true` is observed, the card
 * renders a red breach banner.
 */
const Pill = ({ label, value, tone = "slate", testId }) => {
  const cls = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-800",
    red: "bg-rose-100 text-rose-800",
    amber: "bg-amber-100 text-amber-800",
  }[tone];
  return (
    <div data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${cls}`}>
      <span className="opacity-70">{label}:</span>
      <span className="font-mono">{String(value)}</span>
    </div>
  );
};

export const RedisHealthCard = ({ data, loading = false, error = null, dataTestId = "rl-ops-redis-card" }) => {
  const t = useRacketLabAdminT();
  const breach = data?.customer_facing_allowed === true;

  return (
    <div data-testid={dataTestId} className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
      <header className="flex items-center gap-2">
        <Server className="w-4 h-4 text-slate-500" />
        <h2 className="text-sm font-medium text-slate-800">{t("ops.redis.card_title")}</h2>
      </header>

      {loading && <div data-testid={`${dataTestId}-loading`} className="text-xs text-slate-500">{t("ops.redis.card_loading")}</div>}
      {error && (
        <div data-testid={`${dataTestId}-error`} className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {breach ? (
            <div data-testid={`${dataTestId}-breach`} className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
              <ShieldOff className="w-4 h-4" />
              {t("ops.redis.breach_banner")}
            </div>
          ) : (
            <div data-testid={`${dataTestId}-safe`} className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900">
              <ShieldCheck className="w-4 h-4" />
              {t("ops.redis.safe_banner")}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Pill label="backend" value={data.backend ?? "?"} testId={`${dataTestId}-backend`} tone="slate" />
            <Pill label="healthy" value={data.healthy ?? "?"} testId={`${dataTestId}-healthy`} tone={data.healthy ? "green" : "red"} />
            <Pill label="required" value={data.required ?? "?"} testId={`${dataTestId}-required`} tone="slate" />
            <Pill label="fail_closed" value={data.fail_closed ?? "?"} testId={`${dataTestId}-fail-closed`} tone="slate" />
            <Pill label="kill_switch_active" value={data.kill_switch_active ?? "?"} testId={`${dataTestId}-kill-switch`} tone={data.kill_switch_active ? "amber" : "slate"} />
            <Pill label="customer_facing_allowed" value={data.customer_facing_allowed ?? "?"} testId={`${dataTestId}-customer-facing`} tone={breach ? "red" : "green"} />
          </div>
        </>
      )}
    </div>
  );
};

export default RedisHealthCard;
